import { formatCurrency } from "@/lib/tableFormatters";
import type { NormalizedTableData, TableColumn } from "@/types/table";

type Row = Record<string, unknown>;

function isObject(v: unknown): v is Row {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// Backend returns a 0–1 ratio; multiply by 100 to display as percentage.
export function formatRecoveryPct(value: unknown): string {
  if (value === null || value === undefined) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${(n * 100).toFixed(1)}%`;
}

function buildFlatColumns(labelKey: string, labelHeader: string, sortKey?: string): TableColumn<Row>[] {
  return [
    { key: labelKey, label: labelHeader, align: "left", sortKey, sortType: "text" },
    { key: "billed",       label: "Billed",        align: "right", format: (v) => formatCurrency(v), sortKey: "billed_net" },
    { key: "write_off",    label: "WIP Write Off",  align: "right", format: (v) => formatCurrency(v), sortKey: "write_off" },
    { key: "recovery_pct", label: "Recovery %",     align: "right", format: (v) => formatRecoveryPct(v) },
  ];
}

export function normalizeRecoveryKpiByOffice(response: unknown): NormalizedTableData {
  if (!isObject(response) || !Array.isArray(response.rows)) return { rows: [], columns: [] };

  const rows: Row[] = response.rows.filter(isObject).map((r) => ({
    _label: r.office_location ?? "Unknown",
    billed: r.billed,
    write_off: r.write_off,
    recovery_pct: r.recovery_pct,
  }));

  if (isObject(response.total)) {
    rows.push({
      _label: "Total",
      billed: response.total.billed,
      write_off: response.total.write_off,
      recovery_pct: response.total.recovery_pct,
    });
  }

  return { rows, columns: buildFlatColumns("_label", "Office Location", "office") };
}

export function normalizeRecoveryKpiByServiceLine(response: unknown): NormalizedTableData {
  if (!isObject(response) || !Array.isArray(response.rows)) return { rows: [], columns: [] };

  const rows: Row[] = response.rows.filter(isObject).map((r) => ({
    _label: r.service_line ?? "Unknown",
    billed: r.billed,
    write_off: r.write_off,
    recovery_pct: r.recovery_pct,
  }));

  if (isObject(response.total)) {
    rows.push({
      _label: "Total",
      billed: response.total.billed,
      write_off: response.total.write_off,
      recovery_pct: response.total.recovery_pct,
    });
  }

  return { rows, columns: buildFlatColumns("_label", "Service Line", "service_line") };
}
